import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { showCommonPopupAction } from '@core/js/components/common/popups/CommonPopup';
import { showConfirmationPopupAction } from '@core/js/components/common/popups/ConfirmationPopup';
import { showLoginPopupAction } from '@core/js/components/popups/LoginPopup';
import { showJumioVerifyStatusPopupAction } from '@core/js/components/popups/JumioVerifyPopup';
import { showLogoutNotificationPopupAction } from '@core/js/components/popups/LogoutNotificationPopup';
import { useNavigateToPopupBackgroundPage } from '@core/js/components/system/Router/components/PageRouter/hooks';
import { showTwoFactorAuthenticationPopupAction } from '@core/js/components/popups/TwoFactorAuthenticationPopup';
import { useSmartClosePopups, disablePopupsAction, enablePopupsAction } from '@theme/js/components/common/PopupManager';
import { showBonusBuyInPopupAction } from '@core/js/components/popups/BonusBuyInPopup';
import { showTransparentPopupAction } from '@core/js/components/common/popups/TransparentPopup';
import { showCardPopupAction } from '@core/js/components/common/popups/CardPopup';
import { showGamblingSessionLimitsPopupAction } from '@core/js/components/popups/GamblingSessionLimitsPopup';
import { showReCaptchaPopupAction } from '@core/js/components/popups/ReCaptchaPopup';
import { showQuestionnairePopupAction } from '@core/js/components/popups/QuestionnairePopup';
import { showSimpleDialogInfoPopupAction } from '@core/js/components/popups/SimpleDialogInfoPopup';
import { dispatcher } from 'dispatcher';

const actionsMap = {
    common: showCommonPopupAction,
    confirmation: showConfirmationPopupAction,
    login: showLoginPopupAction,
    jumioVerificationStatus: showJumioVerifyStatusPopupAction,
    logoutNotification: showLogoutNotificationPopupAction,
    twoFactorAuthentication: showTwoFactorAuthenticationPopupAction,
    bonusBuyIn: showBonusBuyInPopupAction,
    transparent: showTransparentPopupAction,
    card: showCardPopupAction,
    gamblingSessionLimits: showGamblingSessionLimitsPopupAction,
    reCaptcha: showReCaptchaPopupAction,
    questionnaire: showQuestionnairePopupAction,
    simpleDialogInfo: showSimpleDialogInfoPopupAction
};

export const usePopupPublicAPI = () => {
    const dispatch = useDispatch();
    const closePopups = useSmartClosePopups();
    const navigateToPopupBackgroundPage = useNavigateToPopupBackgroundPage();

    return useMemo(() => ({
        /**
         * Show a popup. A few popup types are supported:<br><br>
         * 'common' popup by default shows only one button with default text which just closes the popup.
         * supported params: id, content, onClose, btnTitle, closeAll, layer, isModal, articleId, onOverlayClick, layerClassNames, buttons, children, controls, title, onTouchStart, className, classNames, style, iconType<br><br>
         * 'transparent' popup is just like a common popup, but without the popup's background structure.
         * supported params: id, content, onClose, btnTitle, closeAll, layer, isModal, articleId, onOverlayClick, layerClassNames, buttons, children, controls, title, onTouchStart, className, classNames, style, iconType<br><br>
         * 'confirmation' popup by default shows only two buttons with default text, however it is possible to override texts and actions when clicked.
         * supported params: title, onClose, onAccept, controls, onDecline, articleId, content, acceptBtnText, declineBtnText, onOverlayClick, layerClassNames, buttons, children, controls, onTouchStart, className, classNames, style, iconType<br><br>
         * 'login' popup just shows the login popup.
         * supported params: onClose, onLogin, title<br><br>
         * 'jumioVerificationStatus' popup with predefined title and content depends on status property passed to options.
         * supported params:status<br><br>
         * 'logoutNotification' after logout message popup
         * supported params: isLoginAgainPopup, onAccept, onDecline, className<br><br>
         * 'twoFactorAuthentication' popup with TwoFactorAuthentication component
         *  supported params: id, title, templateId, controls, onSuccess, resendHandler, onClose<br><br>
         * 'bonusBuyIn' bonus popup
         *  supported params: title, bonusCode, buyInWebContentId, termAndConditionWebContentId, layerClassNames, buttons, children, controls, title, onTouchStart, className, classNames, style, iconType, onClose, onOverlayClick
         * 'gamblingSessionLimits' popup with GamblingSessionLimits component
         * supported params: onSuccessLimitsSet
         * 'questionnaire' popup with list of questions required before increase player's limits
         *  supported params: id, title, onClose, onSuccess, formName, tkPrefix, daydepositlimit, weekdepositlimit, monthdepositlimit
         * 'simpleDialogInfo' popup to interact with "actionShowSimpleDialogInfo" data structures
         *  supported params: message, actionId, acceptBtnText, declineBtnText, afterSubmitAction
         * @memberOf Playtech.API.popup
         * @function
         * @param [popupType=common] {String} One of 'common', 'confirmation', 'transparent', 'login', 'jumioVerificationStatus', 'logoutNotification', 'twoFactorAuthentication', 'bonusBuyIn', 'gamblingSessionLimits', 'questionnaire', 'simpleDialogInfo'.
         * @param params {Object} Parameters for the popup available for all popupTypes.
         * @param [params.id] {String} id of the popup.
         * @param [params.title] {String} Title of the popup.
         * @param [params.content] {String} Contents of the popup.
         * @param [params.articleId] {String} ID of the Web Content from the CMS to show instead of content.
         * @param [params.isModal=true] {Boolean} Whether to show 'close' button.
         * @param [params.onClose] {Function} Callback function to process when player clicks 'close' button.
         * @param [params.buttons] {Object[]} Buttons of the popup. Empty array "[]" specified for popup without buttons. If buttons param is not specified - popup will show the default "OK" button.
         * @param [params.buttons[].id] {String}
         * @param [params.buttons[].onClick] {Function}
         * @param [params.buttons[].className] {String}
         * @param params.buttons[].text {String}
         * @param [params.buttons[].view] {String} Values: 'primary', 'secondary', 'readonly'.
         * @param [params.buttons[].color] {String} Values: 'success', 'negative', 'info', 'revert', 'generic'.
         * @param [params.buttons[].size] {String} Values: 'large', 'medium', 'small'.
         * @param [params.acceptBtnText] {String} Translation key or text to show on 'accept' button.
         * @param [params.declineBtnText] {String}  Translation key or text to show on 'decline' button.
         * @param [params.onAccept] {Function} Callback function to process when player clicks 'accept' button.
         * @param [params.onDecline] {Function} Callback function to process when player clicks 'decline' button.
         * @param [params.status] {String} Jumio status.
         * @param [params.isLoginAgainPopup] {Boolean} Popup type to 'confirmation'.
         * @param [params.className] {String} Providing styles to popup.
         * @param [params.id] {String} Will be passed to TwoFactorAuthentication component as property.
         * @param [params.title] {String} Will be passed to TwoFactorAuthentication component as property.
         * @param [params.templateId] {String} Will be passed to TwoFactorAuthentication component as property. Values: 'two_factor_email', 'two_factor_sms'.
         * @param [params.controls] {{onPrev: function, onNext: function, label: String}} Will be passed to popup wrapper for controls.
         * @param [params.onSuccess] {Function} Callback after success iapiValidateTwoFactorToken request.
         * @param [params.resendHandler] {Function} Function called on resend code flow.
         * @param [params.btnTitle] {String} Button title.
         * @param [params.closeAll] {Boolean} Whether other popups should be closed.
         * @param [params.layer] {String} Layer witch should be used, this parameter should be used carefully.
         * @param [params.onLogin] {Function} Callback after success login process.
         * @example
         * Playtech.API.popup.showPopup({
         *     title: 'Title',
         *     content: 'Very important message!'
         * });
         * @example
         * Playtech.API.popup.showPopup('confirmation', {
         *     content: 'Would you accept this confirmation?',
         *     onAccept: () => {
         *         // do something if player accepts
         *     }
         * });
         */
        showPopup: (popupType, params) => {
            let action;
            let popupParams;

            if (typeof popupType === 'string') {
                action = actionsMap[popupType] || showCommonPopupAction;
                popupParams = params;
            } else {
                action = showCommonPopupAction;
                popupParams = popupType;
            }

            dispatch(action(popupParams));
        },

        /**
         * @memberOf Playtech.API.popup
         * @function
         * Closes one, several or all popups depending on passed params.
         * @example
         * // will close single popup with ID 'mySpecificPopupID'
         * smartClosePopups('mySpecificPopupID');
         * @example
         * // will close all popups except for PLAYER_FREEZE_ACCOUNT_POPUP and LOGOUT_NOTIFICATION_POPUP
         * smartClosePopups('logout');
         * @example
         * // will close all popups except for WELCOME_POPUP popup and any popups
         * // that are part of ADVANCED_PLAYER_MESSAGE and ADVANCED_PLAYER_MESSAGE_MODAL layers
         * smartClosePopups('gameLaunch');
         * @example
         * // will close popups according to provided criteria
         * smartClosePopups({
         *     popups: ['popupToClose1', 'popupToClose2'],
         *     exclude: ['popupToStay1', 'popupToStay2'],
         *     excludeLayers: ['layerToStay1', 'layerToStay2']
         * });
         * @example
         * // will close all popups
         * smartClosePopups();
         * @param {String|Object} shortcutOrParams
         */
        closePopups,

        /**
         * Disable all popups. Any popups that will be issued will not be visible.
         * But as soon as popups will be enabled -- all issued previous popups will become visible.
         * @memberOf Playtech.API.popup
         * @function
         * @returns {{type: 'DISABLE_POPUPS'}}
         * @example
         * Playtech.API.popup.disablePopups();
         */
        disablePopups: () => dispatch(disablePopupsAction()),

        /**
         * Enable all popups. All popups issued previously will become visible.
         * If this is not desired, please invoke {#closePopups} before.
         * @memberOf Playtech.API.popup
         * @function
         * @returns {{type: 'ENABLE_POPUPS'}}
         * @example
         * Playtech.API.popup.enablePopups();
         */
        enablePopups: () => dispatch(enablePopupsAction()),

        /**
         * Close page which is displayed as popup.
         * @memberOf Playtech.API.popup
         * @function
         * @example
         * Playtech.API.popup.closePageAsPopup();
         */
        closePageAsPopup: () => navigateToPopupBackgroundPage(),
        /* TotalCasino changes - added showLogin method for consistency with BB */
        showLogin: (data) => {
            dispatcher.dispatch('app:message', {
                ID: 'showLogin',
                data
            });
        }
    }), [closePopups, dispatch, navigateToPopupBackgroundPage]);
};
